import { InputValues, PageItem } from "../Interfaces/TestCaseInterface";
import { ErrorReturn, isErrorReturn } from "../Interfaces/UtilityInterface";

export interface ParsedXMLData {
    policyInputs: Record<string, PageItem>,
    pageTemplates: Record<string, Array<string>>,
    userJournies: Record<string, Array<string>>
}

export const parseXMLPolicy = (fileString: string): ErrorReturn | ParsedXMLData => {
    // DOMParser to go over XML policy
    const parser = new DOMParser();

    const xml = parser.parseFromString(fileString, 'text/xml');
    let parsedXMLComponents: ParsedXMLData = { policyInputs: {}, pageTemplates: {}, userJournies: {} };

    let policyInputs: Record<string, PageItem> | ErrorReturn = getUserInputsFromPolicy(xml);

    if (isErrorReturn(policyInputs)) {
        return { error: policyInputs.error };
    } else {
        parsedXMLComponents.policyInputs = policyInputs;
    }

    let emailVerificationDisplayControls: { additionalPolicyInputs: Record<string, PageItem>, displayControlItems: Record<string, Array<string>> } | ErrorReturn
        = getEmailVerificationDisplayControlsFromPolicy(xml);


    let displayControlComponents: Record<string, Array<string>> = {};
    if (isErrorReturn(emailVerificationDisplayControls)) {
        return { error: emailVerificationDisplayControls.error };
    } else {
        parsedXMLComponents.policyInputs = { ...parsedXMLComponents.policyInputs, ...emailVerificationDisplayControls.additionalPolicyInputs };
        displayControlComponents = { ...emailVerificationDisplayControls.displayControlItems };
    }

    let pageTemplates: Record<string, Array<string>> | ErrorReturn
        = getTechnicalProfileDefinitionsFromPolicy(xml, parsedXMLComponents, displayControlComponents);


    if (isErrorReturn(pageTemplates)) {
        return { error: pageTemplates.error };
    } else {
        parsedXMLComponents.pageTemplates = pageTemplates;
    }

    let sisuItems: { additionalPolicyInputs: Record<string, PageItem>, modifiedPageTemplates: Record<string, Array<string>> } | ErrorReturn
        = constructCombinedSISUPages(xml, parsedXMLComponents);


    if (isErrorReturn(sisuItems)) {
        return { error: sisuItems.error };
    } else {
        if(Object.keys(sisuItems.additionalPolicyInputs).length > 0)
            parsedXMLComponents.policyInputs = { ...parsedXMLComponents.policyInputs, ...sisuItems.additionalPolicyInputs };
        parsedXMLComponents.pageTemplates = { ...sisuItems.modifiedPageTemplates };
    }


    const tempPageNames = Object.keys(parsedXMLComponents.pageTemplates);

    for (const key of tempPageNames) {
        // Check whether this is a Combined Sign In and Sign Up page
        if (!parsedXMLComponents.pageTemplates[key].includes('next')) {
            parsedXMLComponents.pageTemplates[key] = parsedXMLComponents.pageTemplates[key].concat(["continue", "cancel"]);
        }
    }

    let userJournies = getUserJourniesFromPolicy(xml, tempPageNames);

    if (isErrorReturn(userJournies)) {
        return { error: userJournies.error };
    } else {
        parsedXMLComponents.userJournies = userJournies;
    }
    

    return parsedXMLComponents;
}

const getUserInputsFromPolicy = (xml: Readonly<Document>): Record<string, PageItem> | { error: string } => {
    // From policy get all ClaimTypes with a UserInputType tag (user interactable B2C elements)
    var inputClaims = Array.from(xml.querySelectorAll('ClaimsSchema>ClaimType'))
        .filter(elem => elem.getElementsByTagName('UserInputType').length > 0);

    if (inputClaims.length === 0) {
        return { error: "No user inputs found in Claims Schema." };
    }

    /* For each figure out which type of input we are dealing with 
       ('null' is for elements we assume are noninteractable, but may have relevant info)
    */
    var policyInputs: Record<string, PageItem> = {};
    inputClaims.forEach(input => {
        var inputId = input.getAttribute("Id")!;
        var userInputType = input.getElementsByTagName('UserInputType')[0].innerHTML;
        var elementInputType: InputValues;

        switch (userInputType) {
            case 'EmailBox':
            case 'TextBox':
            case 'Password':
                elementInputType = InputValues.Text;
                break;
            case 'DropdownSingleSelect':
                elementInputType = InputValues.Dropdown;
                break;
            case 'CheckboxMultiSelect':
                elementInputType = InputValues.Checkbox;
                break;
            default:
                elementInputType = InputValues.Null;
        }

        // Place element in policy inputs data structure
        policyInputs[inputId] = {
            id: inputId,
            input: elementInputType
        }

        //Dropdowns and checkboxes have enumerated options that we need to populate
        if (elementInputType === InputValues.Dropdown || elementInputType === InputValues.Checkbox) {
            var enumeratedOptions: Array<[string, string]> = [];
            if (input.getElementsByTagName('Restriction').length > 0) {
                Array.from(input.getElementsByTagName('Enumeration')).forEach(enumeration => {
                    enumeratedOptions.push([enumeration.getAttribute('Value')!, enumeration.getAttribute('Text')!]);
                })
            }
            policyInputs[inputId].restriction = enumeratedOptions;
        }
    }) // End of inputClaims.forEach

    // Add constant buttons
    policyInputs['next'] = {
        id: 'next',
        input: InputValues.Button
    }

    policyInputs['createAccount'] = {
        id: 'createAccount',
        input: InputValues.Button
    }

    policyInputs['continue'] = {
        id: 'continue',
        input: InputValues.Button
    }

    policyInputs['cancel'] = {
        id: 'cancel',
        input: InputValues.Button
    }

    return policyInputs;
} // End of getUserInputsFromPolicy

const getEmailVerificationDisplayControlsFromPolicy = (xml: Readonly<Document>): ErrorReturn | { additionalPolicyInputs: Record<string, PageItem>, displayControlItems: Record<string, Array<string>> } => {
    // get OTP input from Technical Profiles
    let otpGenerateCodeProfiles: Record<string, string> = {};
    let newDisplayControlInputs: Record<string, PageItem> = {};
    Array.from(xml.getElementsByTagName('TechnicalProfile'))
        .filter(technicalProfile => technicalProfile.getElementsByTagName('Protocol')[0]?.getAttribute('Handler')?.includes('OneTimePasswordProtocolProvider') && Array.from(technicalProfile.getElementsByTagName('Item')).find(item => item.getAttribute('Key') === 'Operation')?.innerHTML === 'GenerateCode')
        .forEach(otpProfiles => {
            let otpProfileName = otpProfiles.getAttribute('Id')!;
            let otpIdentifier = Array.from(otpProfiles.getElementsByTagName('InputClaim')).find(otpInputClaim => otpInputClaim.getAttribute('PartnerClaimType') === 'identifier')?.getAttribute('ClaimTypeReferenceId');
            otpGenerateCodeProfiles[otpProfileName] = otpIdentifier ? otpIdentifier : "identifier";
        });

    // For display controls we need to have little self contained arrays that can be popped into any page
    var tempDisplayControls: Record<string, Array<string>> = {};
    if (xml.getElementsByTagName('DisplayControls').length > 0) {
        var displayDefinitions = Array.from(xml.getElementsByTagName('DisplayControls')[0].getElementsByTagName('DisplayControl'));

        displayDefinitions.forEach(displayGroup => {
            var displayControlName = displayGroup.getAttribute('Id')!;
            tempDisplayControls[displayControlName] = [];
            if (displayGroup.getElementsByTagName('DisplayClaims').length > 0) {
                Array.from(displayGroup.getElementsByTagName('DisplayClaim')).forEach(displayClaim => {
                    tempDisplayControls[displayControlName].push(displayClaim.getAttribute('ClaimTypeReferenceId')!);
                })
            }

            if (displayGroup.getAttribute('UserInterfaceControlType') === 'VerificationControl') {
                // Email verification buttons
                newDisplayControlInputs[`${displayControlName}_but_send_code`] = {
                    id: `${displayControlName}_but_send_code`,
                    input: InputValues.Button
                }

                // Start construction of predefined OTP function
                let generateOTPTechnicalProfileName = Array.from(displayGroup.getElementsByTagName('ValidationClaimsExchangeTechnicalProfile')).find(validationTechnicalProfile => Object.keys(otpGenerateCodeProfiles).includes(validationTechnicalProfile.getAttribute('TechnicalProfileReferenceId')!))?.getAttribute('TechnicalProfileReferenceId');
                let fnOtpId = otpGenerateCodeProfiles[generateOTPTechnicalProfileName ? generateOTPTechnicalProfileName : ''];
                let fnOtpCode = Array.from(displayGroup.getElementsByTagName('DisplayClaim')).find((claim) => claim.getAttribute('ControlClaimType') === 'VerificationCode');

                if (fnOtpId !== undefined && fnOtpCode !== undefined) {
                    newDisplayControlInputs[`${displayControlName}-Fn::otpEmail`] = {
                        id: fnOtpId + ',' + fnOtpCode.getAttribute('ClaimTypeReferenceId')!,
                        input: InputValues.Null
                    }
                } // end construction of predefined otp function

                newDisplayControlInputs[`${displayControlName}_but_verify_code`] = {
                    id: `${displayControlName}_but_verify_code`,
                    input: InputValues.Button
                }

                newDisplayControlInputs[`${displayControlName}_but_send_new_code`] = {
                    id: `${displayControlName}_but_send_new_code`,
                    input: InputValues.Button
                }

                newDisplayControlInputs[`${displayControlName}_but_change_claims`] = {
                    id: `${displayControlName}_but_change_claims`,
                    input: InputValues.Button
                }

                tempDisplayControls[displayControlName].push(`${displayControlName}_but_send_code`);
                tempDisplayControls[displayControlName].push(`${displayControlName}-Fn::otpEmail`);
                tempDisplayControls[displayControlName].push(`${displayControlName}_but_verify_code`);
                tempDisplayControls[displayControlName].push(`${displayControlName}_but_send_new_code`);
                tempDisplayControls[displayControlName].push(`${displayControlName}_but_change_claims`);
            }
        }) // End of displayDefinitions.forEach
    }

    return { additionalPolicyInputs: newDisplayControlInputs, displayControlItems: tempDisplayControls };
} // End of getEmailVerificationDisplayControlsFromPolicy

const getTechnicalProfileDefinitionsFromPolicy = (xml: Readonly<Document>, parsedXMLComponents: Readonly<ParsedXMLData>, displayControlComponents: Readonly<Record<string, Array<string>>>): Record<string, Array<string>> | ErrorReturn => {
    // Find all self asserted technical profiles
    let selfAssertedPages = Array.from(xml.querySelectorAll('TechnicalProfiles>TechnicalProfile'))
        .filter(elem =>
            elem.getElementsByTagName('Protocol').length > 0 && elem.getElementsByTagName('Protocol')[0].getAttribute('Handler')?.includes('.SelfAssertedAttributeProvider')
        );

    let tempPageTemplates: Record<string, Array<string>> = {};

    // Create an entry for each self asserted technical profile with the defined claims
    for (let page of selfAssertedPages) {
        const pageName = page.getAttribute('Id')!;

        tempPageTemplates[pageName] = [];

        if (page.getElementsByTagName('DisplayClaims').length > 0) {
            Array.from(page.getElementsByTagName('DisplayClaim')).forEach(displayClaim => {
                if (displayClaim.hasAttribute('DisplayControlReferenceId'))
                    tempPageTemplates[pageName] = tempPageTemplates[pageName].concat(displayControlComponents[displayClaim.getAttribute('DisplayControlReferenceId')!]);
                else if (parsedXMLComponents.policyInputs[displayClaim.getAttribute('ClaimTypeReferenceId')!] !== undefined)
                    tempPageTemplates[pageName].push(displayClaim.getAttribute('ClaimTypeReferenceId')!);
            })
        }
        else if (page.getElementsByTagName('OutputClaims').length > 0) {
            Array.from(page.getElementsByTagName('OutputClaim')).forEach(outputClaim => {
                if (outputClaim.hasAttribute('ClaimTypeReferenceId') && parsedXMLComponents.policyInputs[outputClaim.getAttribute('ClaimTypeReferenceId')!] !== undefined)
                    tempPageTemplates[pageName].push(outputClaim.getAttribute('ClaimTypeReferenceId')!);
            })
        }
    } // end of Create an entry for each self asserted technical profile

    // Check for overloaded technical profiles (so we can have more distinct and useful page names)
    let overloadedTechnicalProfiles = Array.from(xml.querySelectorAll('TechnicalProfiles>TechnicalProfile'))
        .filter(elem =>
            elem.getElementsByTagName('IncludeTechnicalProfile').length > 0
        );

    for (let overloadedProfile of overloadedTechnicalProfiles) {
        let overloadedProfileBase = overloadedProfile.getElementsByTagName('IncludeTechnicalProfile')[0].getAttribute('ReferenceId')!;
        if (tempPageTemplates[overloadedProfileBase!] !== undefined) {
            let overloadedProfileName = overloadedProfile.getAttribute('Id');

            tempPageTemplates[overloadedProfileName!] = [...tempPageTemplates[overloadedProfileBase!]];
        }
    } // End of get overloaded technical profiles

    return tempPageTemplates;
} // End of getTechnicalProfileDefinitionsFromPolicy

const constructCombinedSISUPages = (xml: Readonly<Document>, parsedXMLComponents: Readonly<ParsedXMLData>): { additionalPolicyInputs: Record<string, PageItem>, modifiedPageTemplates: Record<string, Array<string>> } => {
    // Add page level buttons
    var combinedSignInAndSignUp = Array.from(xml.getElementsByTagName('OrchestrationStep'))
        .filter(step => step.getAttribute('Type') === 'CombinedSignInAndSignUp');


    let policyInputs = { ...parsedXMLComponents.policyInputs };
    let tempPageTemplates = { ...parsedXMLComponents.pageTemplates };

    for (const sisuPage of combinedSignInAndSignUp) {
        var claimsProviderSelectors = Array.from(sisuPage.getElementsByTagName('ClaimsProviderSelection'))
            .filter(claimProvider => claimProvider.getAttribute('TargetClaimsExchangeId') !== null && claimProvider.getAttribute('TargetClaimsExchangeId') !== "");

        var pageButtons = ["next", "createAccount"];
        for (const providerButton of claimsProviderSelectors) {
            let providerButtonId = providerButton.getAttribute('TargetClaimsExchangeId')!;
            pageButtons.push(providerButtonId);

            // If not defined create a policy input for button
            if (policyInputs[providerButtonId] === undefined) {
                policyInputs[providerButtonId] = {
                    id: providerButtonId,
                    input: InputValues.Button
                }
            }
        }

        // Find sisu page name and add the buttons we observed (if we have multiple Combined Sign In Signups using the same ClaimsExchange this may need to be reworked)
        var sisuPageName = sisuPage.getElementsByTagName('ClaimsExchange')[0].getAttribute('TechnicalProfileReferenceId')!;
        tempPageTemplates[sisuPageName] = tempPageTemplates[sisuPageName].concat(pageButtons);
    } // End of for(const sisuPage of combinedSignInAndSignUp)

    return { additionalPolicyInputs: policyInputs, modifiedPageTemplates: tempPageTemplates };
} // End of constructCombinedSISUPages

const getUserJourniesFromPolicy = (xml: Readonly<Document>, tempPageNames: ReadonlyArray<string>): Record<string, Array<string>> | ErrorReturn => {
    // Construct user journies
    let userJourniesXML = Array.from(xml.getElementsByTagName('UserJourney'));

    let tempUserJournies: Record<string, Array<string>> = {};
    let tempSubJournies: Record<string, Array<string>> = {};

    // <SubJourney Id="" Type="Call">
    Array.from(xml.getElementsByTagName('SubJourney')).forEach((subJourney) => {
        let subJourneyType = subJourney.getAttribute('Type');
        if (subJourneyType === "Call") {
            let subJourneyName = subJourney.getAttribute('Id');
            // <OrchestrationStep Order="" Type="ClaimsExchange">
            Array.from(subJourney.getElementsByTagName('OrchestrationStep')).forEach((orchestrationStep) => {
                let orchestrationType = orchestrationStep.getAttribute('Type');
                if (orchestrationType !== 'InvokeSubJourney') {
                    // <ClaimsExchange Id="" TechnicalProfileReferenceId="" />
                    Array.from(orchestrationStep.getElementsByTagName('ClaimsExchange')).forEach((claimsExchange) => {
                        let technicalProfileReferenceId = claimsExchange.getAttribute('TechnicalProfileReferenceId');
                        if (technicalProfileReferenceId === null) {
                            return;
                        }

                        // If a plain orchestration step then we just add therelevant pages to the subjourney
                        if (tempPageNames.includes(technicalProfileReferenceId)) {
                            if (tempSubJournies[subJourneyName!] === undefined) {
                                tempSubJournies[subJourneyName!] = [technicalProfileReferenceId];
                            } else {
                                tempSubJournies[subJourneyName!].push(technicalProfileReferenceId);
                            }
                        }
                        // Else if we are looking at something like an overloaded content definition, we have to go to that Technical profile and figure out which technical profile is actually needed
                        else {
                            let checkIncludedTechnicalProfile = Array.from(
                                xml.getElementsByTagName('TechnicalProfile'))
                                .find(technicalProfile => technicalProfile.getAttribute('Id') === subJourneyName);
                            if (checkIncludedTechnicalProfile !== undefined) {
                                var actualTechnicalProfileToAdd = checkIncludedTechnicalProfile.getElementsByTagName('IncludeTechnicalProfile');
                                if (actualTechnicalProfileToAdd.length > 0 && tempPageNames.includes(actualTechnicalProfileToAdd[0].getAttribute('ReferenceId')!)) {
                                    if (tempSubJournies[subJourneyName!] === undefined) {
                                        tempSubJournies[subJourneyName!] = [actualTechnicalProfileToAdd[0].getAttribute('ReferenceId')!];
                                    } else {
                                        tempSubJournies[subJourneyName!].push(actualTechnicalProfileToAdd[0].getAttribute('ReferenceId')!);
                                    }
                                }
                            }
                        }
                    })
                }
            })
        }
    }) // end of sub-journies

    // <UserJourney Id=''>
    userJourniesXML.forEach((journey) => {
        let journeyName = journey.getAttribute("Id");
        if (journeyName === null) {
            return;
        }

        // <OrchestrationStep Order="" Type="ClaimsExchange">
        Array.from(journey.getElementsByTagName('OrchestrationStep')).forEach((orchestrationStep) => {
            let orchestrationType = orchestrationStep.getAttribute('Type');
            if (orchestrationType !== 'InvokeSubJourney') {
                // <ClaimsExchange Id="" TechnicalProfileReferenceId="" />
                Array.from(orchestrationStep.getElementsByTagName('ClaimsExchange')).forEach((claimsExchange) => {
                    let technicalProfileReferenceId = claimsExchange.getAttribute('TechnicalProfileReferenceId');
                    if (technicalProfileReferenceId === null) {
                        return;
                    }
                    if (tempPageNames.includes(technicalProfileReferenceId)) {
                        if (tempUserJournies[journeyName!] === undefined) {
                            tempUserJournies[journeyName!] = [technicalProfileReferenceId];
                        } else {
                            tempUserJournies[journeyName!].push(technicalProfileReferenceId);
                        }
                    }
                })
            }
            // Check subjourney contents
            else {
                let subjourneyName = orchestrationStep.getElementsByTagName('Candidate')[0].getAttribute('SubJourneyReferenceId');
                if (Object.keys(tempSubJournies).includes(subjourneyName!)) {
                    if (tempUserJournies[journeyName!] === undefined) {
                        tempUserJournies[journeyName!] = [...tempSubJournies[subjourneyName!]];
                    } else {
                        tempUserJournies[journeyName!] = tempUserJournies[journeyName!].concat([...tempSubJournies[subjourneyName!]]);
                    }
                }
            }
        })
    }) // end user journey creation

    return tempUserJournies;
}